import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Calculator, Smartphone, ShieldCheck, Zap, BadgePercent, Globe2 } from "lucide-react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/fdfb0ae7-76b4-404f-a06f-72ecb18117c6/images/fcd843641e9ae6743c25427c6a5209c7295bbd81cc7f3c81a5bdedd0b9fec38c.png";
const PHONES_3D = "https://static.prod-images.emergentagent.com/jobs/fdfb0ae7-76b4-404f-a06f-72ecb18117c6/images/d0d0a29ee18a19fed1918a1138cd1b2b251c45acbc95aec662dd220bebd40f31.png";
const DEVICE_FALLBACK = "https://images.pexels.com/photos/3945672/pexels-photo-3945672.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Derive a short badge label from the v1.3+ promo schema (which has no top-level promo_type).
// Prefer phone_pricing scenarios → tablet_pricing → home_internet_pricing → generic "Promo".
function promoBadge(p) {
  const pp = p?.phone_pricing;
  if (pp) {
    if (pp.finance?.enabled) return "Finance";
    for (const g of ["switch", "join", "no_hassle"]) {
      const grp = pp[g];
      if (grp && (grp.single_month?.enabled || grp.multi_month?.enabled || grp.multi_line?.enabled)) {
        return g === "no_hassle" ? "No hassle" : g.charAt(0).toUpperCase() + g.slice(1);
      }
    }
    if (pp.full_retail?.enabled) return "Full retail";
  }
  if (p?.tablet_pricing?.bundle?.enabled || p?.tablet_pricing?.standalone?.enabled) return "Tablet";
  if (p?.home_internet_pricing?.bundle?.enabled || p?.home_internet_pricing?.standalone?.enabled) return "Home Internet";
  return "Promo";
}

export default function Landing() {
  const { t } = useT();
  const [promos, setPromos] = useState([]);
  const [devicesMap, setDevicesMap] = useState({});
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [p, d, pl] = await Promise.all([
          api.get("/promos", { params: { active_only: true } }),
          api.get("/devices", { params: { active_only: true } }),
          api.get("/plans", { params: { active_only: true } }),
        ]);
        const map = {};
        (d.data || []).forEach((x) => (map[x.id] = x));
        setDevicesMap(map);
        setPromos((p.data || []).slice(0, 6));
        setPlans((pl.data || []).slice(0, 3));
      } catch (_) { /* noop */ }
    })();
  }, []);

  return (
    <main data-testid="page-landing">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200" data-testid="hero-section">
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="tw-container relative grid grid-cols-1 lg:grid-cols-12 gap-10 py-20 lg:py-28">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="tw-overline"
            >
              <span className="inline-flex items-center gap-2"><Zap className="h-3 w-3" /> {t("hero_overline")}</span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.05 }}
              className="mt-4 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter text-[#0B132B] leading-[0.95]"
            >
              {t("hero_h1_a")} <br/>
              <span className="relative inline-block">
                {t("hero_h1_b")}
                <span className="absolute -bottom-1 left-0 h-2 w-full bg-[#00E5FF]" aria-hidden="true" />
              </span>{" "}
              {t("hero_h1_c")}
            </motion.h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl">
              {t("hero_body")}
            </p>
            <div className="mt-8 flex flex-wrap gap-4" data-testid="hero-ctas">
              <Link to="/wizard" className="btn-brand" data-testid="cta-start-bundle">
                Build phone + plan bundle <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { k: t("stat_plans"), tid: "plans", v: plans.length || "—" },
                { k: t("stat_promos"), tid: "promos", v: promos.length || "—" },
                { k: t("stat_devices"), tid: "devices", v: Object.keys(devicesMap).length || "—" },
              ].map((s) => (
                <div key={s.tid} className="border-l-2 border-[#00E5FF] pl-3" data-testid={`hero-stat-${s.tid}`}>
                  <div className="mono text-3xl font-bold text-[#0B132B] ticker-num">{s.v}</div>
                  <div className="tw-overline mt-1">{s.k}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="relative"
            >
              <div className="aspect-[4/5] bg-[#0B132B] border border-slate-200 overflow-hidden relative">
                <img src={PHONES_3D} alt="Phones" className="absolute inset-0 w-full h-full object-cover opacity-90" />
                <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                  <span className="badge-pill badge-teal">{t("live_quote")}</span>
                  <span className="mono text-xs text-[#00E5FF] tracking-widest">// TW-CALC</span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#0B132B] via-[#0B132B]/85 to-transparent">
                  <div className="tw-overline !text-[#00E5FF]">{t("sample_total")}</div>
                  <div className="mono text-5xl font-bold text-white mt-1 ticker-num">$94.50</div>
                  <div className="text-xs text-slate-300 mt-1">3 {t("lines")} · {t("unlimited")} · Hotspot</div>
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 hidden md:block bg-white border border-slate-200 px-4 py-3 shadow-[6px_6px_0_#0B132B]">
                <div className="tw-overline">{t("as_of_today")}</div>
                <div className="text-sm font-semibold mono">{new Date().toLocaleDateString()}</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Feature row */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="tw-container grid grid-cols-1 md:grid-cols-3">
          {[
            { icon: Calculator, t: t("feature_math_t"), d: t("feature_math_d") },
            { icon: BadgePercent, t: t("feature_promo_t"), d: t("feature_promo_d") },
            { icon: ShieldCheck, t: t("feature_save_t"), d: t("feature_save_d") },
          ].map((f, i) => (
            <div key={i} className={`p-10 ${i < 2 ? "md:border-r border-slate-200" : ""}`} data-testid={`feature-${i}`}>
              <f.icon className="h-7 w-7 text-[#008B99]" strokeWidth={1.6} />
              <div className="mt-4 text-xl font-bold text-[#0B132B]">{f.t}</div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Promos section removed per product owner request. */}

      {/* CTA banner */}
      <section className="bg-[#0B132B] text-white" data-testid="cta-banner">
        <div className="tw-container py-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="tw-overline !text-[#00E5FF]">{t("cta_banner_overline")}</div>
            <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight">
              {t("cta_banner_title_a")} <span className="text-[#00E5FF]">{t("cta_banner_title_b")}</span> {t("cta_banner_title_c")}
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/estimator" className="btn-brand" data-testid="cta-banner-estimator">
              {t("cta_open_estimator")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
